import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Phone, Users, Check, CircleDot, Circle, Copy, Share2, MessageCircle } from "lucide-react";

function NeedStatusIcon({ s }) {
  if (s === "fulfilled") return <Check className="w-4 h-4 text-[#670003]" strokeWidth={3} />;
  if (s === "partial") return <CircleDot className="w-4 h-4 text-[#141414]" strokeWidth={2.5} />;
  return <Circle className="w-4 h-4 text-[#141414]/40" strokeWidth={2} />;
}

export default function BeneficiaryDetail() {
  const { id } = useParams();
  const { t } = useApp();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/beneficiaries/${id}`).then((r) => setB(r.data)).catch(() => setErr(true));
  }, [id]);

  const url = typeof window !== "undefined" ? window.location.href : "";

  const shareText = () => {
    if (!b) return "";
    return t.beneficiaries.shareMessage
      .replace("{pic}", b.pic_name)
      .replace("{kota}", b.kota)
      .replace("{kabupaten}", b.kabupaten)
      .replace("{people}", b.people_count) + " " + url;
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(url);
    toast.success(t.beneficiaries.linkCopied);
  };

  const shareWA = () => {
    const wa = `https://wa.me/?text=${encodeURIComponent(shareText())}`;
    window.open(wa, "_blank", "noopener,noreferrer");
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `Lepo Gete · ${b.pic_name}`, text: shareText(), url }); return; }
      catch { /* user cancelled */ }
    }
    copyLink();
  };

  if (err) {
    return (
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-16 text-center">
        <div className="font-display text-3xl">404</div>
        <p className="mt-2 text-[#141414]/70">Data tidak ditemukan.</p>
        <Link to="/beneficiaries" className="inline-block mt-6 flat-btn-primary rounded-full px-5 py-2.5 text-sm font-semibold">← {t.nav.beneficiaries}</Link>
      </div>
    );
  }

  if (!b) {
    return <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 text-[#141414]/50">Loading…</div>;
  }

  const statusMap = {
    helped: "bg-[#670003] text-[#fff0d2] border-[#670003]",
    in_progress: "bg-[#141414] text-[#fff0d2] border-[#141414]",
    pending: "bg-transparent text-[#141414] border-[#141414]",
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Link to="/beneficiaries" data-testid="detail-back" className="inline-flex items-center gap-1 text-xs font-semibold text-[#141414]/60 hover:text-[#141414] mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> {t.nav.beneficiaries}
      </Link>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#670003] flex items-center gap-1">
            <MapPin className="w-3 h-3" strokeWidth={2.5} /> {b.kota}, {b.kabupaten}
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">{b.pic_name}</h1>
          <div className="flex items-center gap-4 mt-3 text-sm text-[#141414]/80">
            <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" strokeWidth={2} /> {b.pic_number}</span>
            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" strokeWidth={2} /> {b.people_count} orang</span>
          </div>
        </div>
        <span className={`inline-flex items-center text-[11px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full border self-start ${statusMap[b.status]}`}>
          {t.beneficiaries.statuses[b.status]}
        </span>
      </div>

      {/* Overall progress */}
      <div className="flat-border rounded-2xl bg-white p-6 mb-6">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.beneficiaries.fields.progress}</div>
          <div className="font-display text-3xl">{Math.round(b.progress || 0)}%</div>
        </div>
        <div className="h-2 progress-track rounded-full overflow-hidden">
          <div className="h-full bg-[#670003] transition-all duration-500" style={{ width: `${b.progress || 0}%` }} />
        </div>
        <div className="text-xs text-[#141414]/60 mt-2">{b.needs_fulfilled}/{b.needs_total} kebutuhan lunas</div>
      </div>

      {/* Needs list */}
      {(b.needs || []).length > 0 && (
        <div className="mb-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-3">{t.beneficiaries.fields.needs}</div>
          <div className="space-y-2">
            {b.needs.map((n, i) => {
              const rq = Number(String(n.requested_qty).replace(/[^0-9.]/g, "")) || 0;
              const fq = Number(String(n.fulfilled_qty).replace(/[^0-9.]/g, "")) || 0;
              const pct = rq > 0 ? Math.min(100, (fq / rq) * 100) : (n.status === "fulfilled" ? 100 : n.status === "partial" ? 50 : 0);
              return (
                <div key={i} data-testid={`detail-need-${i}`} className="flat-border rounded-xl bg-white p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <NeedStatusIcon s={n.status} />
                    <div className="font-semibold flex-1">{n.name}</div>
                    <div className="text-xs font-mono text-[#141414]/70">{n.fulfilled_qty} / {n.requested_qty}</div>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-[#670003] w-20 text-right">{t.beneficiaries.needStatus[n.status]}</span>
                  </div>
                  <div className="h-1.5 progress-track">
                    <div className="h-full bg-[#670003]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(b.details || b.help_provided) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {b.details && (
            <div className="flat-border rounded-xl bg-white p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-1.5">{t.beneficiaries.fields.details}</div>
              <div className="text-sm">{b.details}</div>
            </div>
          )}
          {b.help_provided && (
            <div className="flat-border rounded-xl bg-white p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-1.5">{t.beneficiaries.fields.help_provided}</div>
              <div className="text-sm">{b.help_provided}</div>
            </div>
          )}
        </div>
      )}

      {/* Share bar */}
      <div className="flat-border rounded-2xl bg-[#fff0d2] p-4 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#670003] sm:mr-auto">{t.beneficiaries.share}</div>
        <button onClick={shareWA} data-testid="share-wa" className="flat-btn-primary rounded-full px-4 py-2 text-sm font-semibold flex items-center justify-center gap-2">
          <MessageCircle className="w-4 h-4" strokeWidth={2} /> {t.beneficiaries.shareWhatsApp}
        </button>
        <button onClick={copyLink} data-testid="share-copy" className="flat-btn bg-white rounded-full px-4 py-2 text-sm font-semibold flex items-center justify-center gap-2">
          <Copy className="w-4 h-4" strokeWidth={2} /> {t.beneficiaries.copyLink}
        </button>
        <button onClick={nativeShare} data-testid="share-native" className="flat-btn bg-white rounded-full px-4 py-2 text-sm font-semibold flex items-center justify-center gap-2 sm:hidden">
          <Share2 className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
